import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Plane, Calendar, TrendingUp, CalendarRange, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";

const fmtDays = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};
const fmtDate = (s) => formatDate(s);

export default function EmployeeVacationsTab({ employeeId }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const fetchBalance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/employees/${employeeId}/vacation-balance`);
      setBalance(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el saldo de vacaciones");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const computedGrantDays = (() => {
    const { start_date, end_date } = form;
    if (!start_date) return 0;
    const s = new Date(start_date + "T00:00:00");
    const e = new Date((end_date || start_date) + "T00:00:00");
    if (isNaN(s) || isNaN(e) || e < s) return 0;
    return Math.round((e - s) / 86400000) + 1; // días naturales inclusivos
  })();

  const submitGrant = async () => {
    if (!form.start_date) {
      toast.error("Seleccione la fecha (o el rango) de las vacaciones");
      return;
    }
    if (form.end_date && form.end_date < form.start_date) {
      toast.error("La fecha de fin no puede ser anterior a la de inicio");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/vacation-grants`, {
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        notes: form.notes.trim() || undefined,
      });
      toast.success("Otorgamiento histórico registrado");
      setDialogOpen(false);
      setForm({ start_date: "", end_date: "", notes: "" });
      fetchBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const deleteGrant = async (id) => {
    if (!window.confirm("¿Eliminar este otorgamiento histórico?")) return;
    try {
      await api.delete(`/vacation-grants/${id}`);
      toast.success("Registro eliminado");
      fetchBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const downloadReceipt = async (incidentId) => {
    try {
      const res = await api.get(`/incidents/${incidentId}/vacation-receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir el comprobante");
    }
  };

  if (loading || !balance) {
    return <div className="py-12 text-center text-sm text-zinc-400">Cargando…</div>;
  }

  const cards = [
    { label: "Días ganados", value: fmtDays(balance.accrued_days), icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Días otorgados", value: fmtDays(balance.taken_days), icon: Calendar, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Disponibles", value: fmtDays(balance.available_days), icon: Plane, color: "text-zinc-900", bg: "bg-zinc-100", emphasis: true },
  ];

  const periods = balance.accrual_periods || [];
  const hist = balance.historical_grants || [];
  const incGrants = balance.incident_grants || [];

  return (
    <div className="space-y-6" data-testid="vacations-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cards.map((c, idx) => {
          const Icon = c.icon;
          return (
            <Card key={idx} className={c.emphasis ? "border-zinc-900 border-2" : ""}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs uppercase tracking-wide text-zinc-500">{c.label}</span>
                  <div className={`p-1.5 rounded-md ${c.bg}`}>
                    <Icon className={`w-3.5 h-3.5 ${c.color}`} strokeWidth={1.8} />
                  </div>
                </div>
                <div className={`text-2xl font-semibold ${c.color}`} data-testid={`vac-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {c.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 text-xs text-zinc-500">
        <strong className="text-zinc-700">Fecha de ingreso:</strong> {formatDate(balance.hire_date)} ·{" "}
        <strong className="text-zinc-700">Cuota por ciclo:</strong> {balance.vacation_days_per_year} días naturales por cada año de servicio cumplido (aniversario de ingreso; Art. 153 Código de Trabajo). En el año en curso se acumula <strong className="text-zinc-700">1 día por cada mes completo laborado</strong> (aniversario mensual, sin fracciones). Disponibles = Ganados − Otorgados (históricos + incidencias).
      </div>

      {/* Días ganados por periodo */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
            <div className="flex items-start gap-2">
              <div className="p-1.5 rounded-md bg-emerald-50 mt-0.5">
                <CalendarRange className="w-3.5 h-3.5 text-emerald-600" strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-zinc-900">Días ganados por periodo</h3>
                <p className="text-xs text-zinc-500">Cada año de servicio cumplido otorga la cuota completa; el año en curso suma 1 día por mes completo.</p>
              </div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700">#</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700">Periodo</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700">Estado</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700 text-right">Días ganados</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {periods.map((p) => (
                <TableRow key={p.index} className="border-zinc-100" data-testid={`vac-period-${p.index}`}>
                  <TableCell className="text-sm text-zinc-500">{p.index}</TableCell>
                  <TableCell className="text-sm text-zinc-800 font-mono">{fmtDate(p.start)} → {fmtDate(p.end)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={p.completed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}>
                      {p.completed ? "Completo" : "En curso"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-900 font-semibold text-right">{fmtDays(p.days)}</TableCell>
                </TableRow>
              ))}
              {periods.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-sm text-zinc-400">Aún no hay días acumulados.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {periods.length > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100 bg-zinc-50">
              <span className="text-sm font-semibold text-zinc-700">Total ganados</span>
              <span className="text-sm font-bold text-emerald-700" data-testid="vac-periods-total">{fmtDays(balance.accrued_days)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Otorgamientos */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
            <div className="flex items-start gap-2">
              <div className="p-1.5 rounded-md bg-amber-50 mt-0.5">
                <Plane className="w-3.5 h-3.5 text-amber-600" strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-zinc-900">Otorgamientos</h3>
                <p className="text-xs text-zinc-500 max-w-xl">
                  Días de vacaciones ya disfrutados. Los <strong>nuevos</strong> se registran por Incidencias (para reflejarse en planilla). Los <strong>históricos</strong> (anteriores al sistema) se registran aquí.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setDialogOpen(true)}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="vac-add-grant"
            >
              <Plus className="w-4 h-4 mr-1" /> Registrar histórico
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700 text-right">Días</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700">Origen</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-zinc-700">Detalle</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Incidencias (nuevas) */}
              {incGrants.map((g) => (
                <TableRow key={`inc-${g.id}`} className="border-zinc-100" data-testid={`vac-inc-${g.id}`}>
                  <TableCell className="text-sm font-mono">{fmtDate(g.start_date)}{g.end_date && g.end_date !== g.start_date ? ` → ${fmtDate(g.end_date)}` : ""}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-red-600">− {fmtDays(g.days)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                      Incidencia{g.receipt_number ? ` · ${g.receipt_number}` : ""}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600">
                    <span className={`text-xs mr-2 ${g.status === "approved" ? "text-emerald-600" : "text-amber-600"}`}>
                      {g.status === "approved" ? "Aprobada" : "Pendiente"}
                    </span>
                    {g.notes || ""}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 px-2 text-indigo-600 hover:bg-indigo-50"
                      onClick={() => downloadReceipt(g.id)}
                      title="Descargar comprobante"
                      data-testid={`vac-inc-${g.id}-receipt`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {/* Históricos */}
              {hist.map((g) => (
                <TableRow key={`hist-${g.id}`} className="border-zinc-100" data-testid={`vac-hist-${g.id}`}>
                  <TableCell className="text-sm font-mono">
                    {g.end_date && g.end_date !== g.start_date
                      ? `${fmtDate(g.start_date || g.granted_date)} → ${fmtDate(g.end_date)}`
                      : fmtDate(g.start_date || g.granted_date)}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold text-red-600">− {fmtDays(g.days)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200">Histórico</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600">
                    {g.notes || "—"}
                    {g.created_by && (
                      <span className="block text-[11px] text-zinc-400 mt-0.5" data-testid={`vac-hist-${g.id}-by`}>
                        Registrado por {g.created_by}
                        {g.created_at ? ` · ${fmtDate(g.created_at)}` : ""}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                      onClick={() => deleteGrant(g.id)}
                      data-testid={`vac-hist-${g.id}-del`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {incGrants.length === 0 && hist.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-sm text-zinc-400">
                    No hay otorgamientos registrados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog registrar histórico */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="vac-grant-dialog">
          <DialogHeader>
            <DialogTitle>Registrar días otorgados (histórico)</DialogTitle>
            <DialogDescription>
              Para vacaciones ya disfrutadas antes de usar el sistema. Restan del saldo disponible y no afectan la planilla actual.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">Fecha de inicio</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  max={form.end_date || undefined}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="vac-grant-start"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">Fecha de fin</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  min={form.start_date || undefined}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="vac-grant-end"
                />
              </div>
            </div>
            <p className="text-[11px] text-zinc-400 -mt-1">
              Para un solo día, deje la fecha de fin vacía o igual a la de inicio.
            </p>
            <div
              className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3"
              data-testid="vac-grant-days-box"
            >
              <span className="text-[11px] uppercase tracking-wide font-medium text-emerald-700">Días a otorgar (calculado)</span>
              <span className="text-2xl font-extrabold text-emerald-700 tabular-nums" data-testid="vac-grant-days-count">
                {computedGrantDays > 0 ? `${computedGrantDays}` : "—"}
                {computedGrantDays > 0 && (
                  <span className="text-sm font-semibold ml-1">día{computedGrantDays === 1 ? "" : "s"}</span>
                )}
              </span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Nota (opcional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Ej.: Semana Santa 2023, permiso de 2 días, etc."
                data-testid="vac-grant-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={submitGrant}
              disabled={saving}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="vac-grant-save"
            >
              {saving ? "Guardando…" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
